<script lang="ts">
  import {balances} from "$lib/stores";
  import type {IBalance, INetwork} from "$lib/interfaces/IBalance";
  import {onMount} from "svelte";
  import CopyIcon from "$lib/icons/CopyIcon.svelte";
  import {QRCode} from "@castlenine/svelte-qrcode";
  import CustomDropdown from "$lib/elements/CurrencyDropdown.svelte";
  import {toasts} from "$lib/stores/toastStores";
  import BonusOffers from "$lib/components/ModalContents/DepositWithdraw/Deposit/BonusOffers.svelte";
  import Button from "$lib/elements/Button.svelte";

  // Types
  interface ChangellyConfig {
    merchantId: string;
    defaultAmount: number;
    color: string;
    headerId: number;
    version: number;
  }

  type CryptoMap = {
    [key: string]: string;
  };

  // Constants
  const CHANGELLY_CONFIG: ChangellyConfig = {
    merchantId: 'HLyp8EWJPJm5tzz0',
    defaultAmount: 200,
    color: 'ec3c38',
    headerId: 1,
    version: 3
  };

  const CRYPTO_MAP: CryptoMap = {
    'TRX': 'trx',
    'USDT': 'usdtrx',
    'USDTT': 'usdtrx', // Add mapping for USDTT
    'USDTE': 'usderc20', // Add mapping for USDTE
    'Bitcoin': 'btc',
    'Ethereum': 'eth',
    'USDC': 'usdc',
    'Litecoin': 'ltc',
    'Dogecoin': 'doge',
    'Ripple': 'xrp'
  } as const;

  const LOADING_DELAY = 1000;
  const USDT_VARIANTS = ['USDTE', 'USDTT'];

  // State
  let storedBalances: IBalance[] | null = $state(null);
  let loading = true;
  let buyCryptoUrl = $state('');
  let buyNowEnabled = $state(false);
  let walletAddressTag = '';
  let buyCryptoCurrency = CRYPTO_MAP.USDT;
  let selectedAddressForBuyNow = $state('');
  let selectedAddressForBuyNowLabel: IBalance | null = $state(null);

  
  function getUSDTNetworks(balances: IBalance[]): INetwork[] {
    return USDT_VARIANTS
      .map(variant => balances.find(b => b.shortName === variant))
      .filter((balance): balance is IBalance => balance !== undefined)
      .flatMap(balance => balance.cryptoNetworks);
  }

  
  function getNetworkAddress(networks: INetwork[], preferredNetwork: string): string {
    const network = networks.find(n => n.name === preferredNetwork);
    return network?.address || networks[0]?.address || '';
  }

  function createChangellyUrl(address: string, crypto: string, tag?: string): string {
    const params = new URLSearchParams({
      from: '*',
      to: crypto,
      amount: CHANGELLY_CONFIG.defaultAmount.toString(),
      address,
      fromDefault: 'usd',
      toDefault: crypto,
      merchant_id: CHANGELLY_CONFIG.merchantId,
      v: CHANGELLY_CONFIG.version.toString(),
      'type': 'no-rev-share',
      color: CHANGELLY_CONFIG.color,
      headerId: CHANGELLY_CONFIG.headerId.toString(),
      logo: 'hide',
      buyButtonTextId: '2',
      readOnlyDestinationAddress: 'true'
    });

    if (tag) {
      params.append('payment_id', tag);
    }

    return `https://widget.changelly.com?${params.toString()}`;
  }

  function changeCryptoBuyNow(balance: IBalance) {
    try {
      selectedAddressForBuyNowLabel = balance;
    
      // For USDT, always use usdtrx (Tron network)
      if (balance.name === 'USDT') {
        buyCryptoCurrency = 'usdtrx';
        // Always use Tron network for USDT
        selectedAddressForBuyNow = getNetworkAddress(balance.cryptoNetworks, 'TRON');
      } else {
        buyCryptoCurrency = CRYPTO_MAP[balance.name] || CRYPTO_MAP.USDT;
        
        if (balance.name === 'USDC') {
          selectedAddressForBuyNow = getNetworkAddress(balance.cryptoNetworks, 'ETH');
        } else {
          selectedAddressForBuyNow = balance.cryptoNetworks[0]?.address || '';
        }
      }

      walletAddressTag = balance.name === 'Ripple' ? balance.cryptoNetworks[0]?.tag || '' : '';
    } catch (error) {
      console.error('Error changing crypto:', error);
      toasts.send('Error selecting cryptocurrency', 'error');
    }
  }

  function generateBuyNowUrl() {
    if (!selectedAddressForBuyNow) {
      toasts.send('No valid address found', 'error');
      return;
    }

    try {
      buyCryptoUrl = createChangellyUrl(
        selectedAddressForBuyNow,
        buyCryptoCurrency,
        walletAddressTag
      );
      buyNowEnabled = true;
    } catch (error) {
      console.error('Error generating buy URL:', error);
      toasts.send('Error generating buy link', 'error');
    }
  }

  // Subscription handler
  function handleBalancesUpdate(value: IBalance[] | null) {
    if (!value) return;

    try {
      // Filter out USDTT/USDTE variants from the dropdown, keep only USDT
      const filteredBalances = value.filter(balance => 
        balance.isCrypto && 
        !USDT_VARIANTS.includes(balance.shortName)
      );

      // Find the USDTT balance to get its networks
      const usdttBalance = value.find(b => b.shortName === "USDTT");
      
      // Find or create the USDT balance
      let usdtBalance = filteredBalances.find(b => b.name === "USDT");
      
      // If USDTT exists, ensure USDT has its networks
      if (usdttBalance && usdtBalance) {
        usdtBalance.cryptoNetworks = usdttBalance.cryptoNetworks;
      }
      
      storedBalances = filteredBalances;

      if (storedBalances.length > 0) {
        // Default to USDT if available
        const usdt = storedBalances.find(b => b.name === 'USDT');
        if (usdt) {
          changeCryptoBuyNow(usdt);
        } else {
          changeCryptoBuyNow(storedBalances[0]);
        }
      }
    } catch (error) {
      console.error('Error processing balances:', error);
      toasts.send('Error loading cryptocurrencies', 'error');
    } finally {
      setTimeout(() => {
        loading = false;
      }, LOADING_DELAY);
    }
  }

  onMount(() => {
    const unsubscribe = balances.subscribe(handleBalancesUpdate);
    return unsubscribe;
  });
</script>

{#if selectedAddressForBuyNowLabel && storedBalances}
  <div class="flex flex-col justify-center items-center mt-2">
    {#if !buyNowEnabled}
      <div class="flex gap-3 mb-4">
        <CustomDropdown 
          selected={selectedAddressForBuyNowLabel} 
          selectedTitle="name" 
          items={storedBalances} 
          hasIcon={true} 
          on:selected={(item) => changeCryptoBuyNow(item.detail)} 
          id="cryptoCurrencyDropdown" 
        />
      </div>

      <div class="flex flex-col items-center bg-gradient-to-b bg-primary-lightBlue rounded-xl px-4 py-5 space-y-2.5 w-full max-w-80">
        <img src="/deposit/changelly.svg" alt="Changelly logo" height="36" width="121">
        <div class="text-sm flex justify-between w-full text-white">
          <div>Transaction Fee:</div>
          <div>5%</div>
        </div>
        <div class="text-sm flex justify-between w-full text-white">
          <div>Processing Time:</div>
          <div>~10 Min</div>
        </div>
        <div class="text-sm flex justify-between w-full text-white">
          <div>Verification:</div>
          <div>Only First Time</div>
        </div>
        <div class="flex gap-3 mt-3">
          <img src="/deposit/mastercard.svg" class="w-14" alt="Mastercard">
          <img src="/deposit/visa.svg" class="w-14" alt="Visa">
          <img src="/deposit/apple-pay.svg" class="w-14" alt="Apple Pay">
          <img src="/deposit/google-pay.svg" class="w-14" alt="Google Pay">
        </div>
      </div>
      <div class="flex justify-center mt-3">
        <Button 
          on:click={generateBuyNowUrl}
          disabled={!selectedAddressForBuyNow}
        >
          Buy Now
        </Button>
      </div>
    {:else}
      <iframe 
        src={buyCryptoUrl || "/placeholder.svg"} 
        width="100%" 
        style="flex-grow: 1; height: 400px; border-radius: 16px" 
        frameborder='none' 
        allow="camera"
        title="Changelly widget"
      >
        Can't load widget
      </iframe>

      <div class="flex justify-center mt-3">
        <Button on:click={() => buyNowEnabled = false}>Change Crypto</Button>
      </div>
    {/if}

    <div class="w-10/12">
      <BonusOffers type="CRYPTO" />
    </div>
  </div>
{/if}